import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import { get, set, unionBy } from "lodash";
import moment from "moment";
import { FC, FormEvent, useContext, useState } from "react";
import useAsyncEffect from "use-async-effect";
import { HistoryProps } from "../..";
import { medicalsApi } from "../../../../../../api/medicalsApi";
import { providerApi } from "../../../../../../api/providerApi";
import ComponentLoader from "../../../../../../components/ComponentLoader";
import {
  NotificationVariant,
  NotificationsContext,
} from "../../../../../../components/dashboard/NotificationsProvider";
import TypeAheadInput from "../../../../../../components/form/TypeAheadInput/TypeAheadInput";
import Quantity from "../../../../../../components/widgets/inputs/Quantity";
import CodingSystems from "../../../../../../constants/CodingSystems";
import useComponentLoader from "../../../../../../hooks/useComponentLoader";
import useMedicalEncounter from "../../../../../../hooks/useMedicalEncounter";
import {
  createEncounterParam,
  createReference,
} from "../../../../../../utils/DataTransformationUtils";
import { createSlug } from "../../../../../../utils/createSlug";
import DialogPrompt from "../../../components/Dialog";
import { emptyHistoryItems } from "../HistoryHelpers";
import DateOptions, {
  DateValueType,
} from "../../../../../../components/form/inputs/DateOptions";

const hospitalizationHistoryStatuses = [
  "planned",
  "active",
  "finished",
  "entered-in-error",
  "cancelled",
];

function generateHospitalizationStatusDisplay(status) {
  switch (status) {
    case "planned":
      return "Planned";
    case "active":
      return "Active";
    case "finished":
      return "Finished";
    case "entered-in-error":
      return "Entered in Error";
    case "cancelled":
      return "Cancelled";
    default:
      return status;
  }
}

const blankServiceProviders = {
  display: "",
  identifier: "",
  reference: "",
  type: "Organization",
};

const HospitalizationHistoryForm: FC<HistoryProps> = ({
  item,
  patientReference,
  disabled,
  removeHistoryItem,
  setSaved,
  updateHistoryItem,
}) => {
  const {
    encounterReference,
    setEncounterToInProgress,
    refreshPatientHistory,
  } = useMedicalEncounter();
  const { setNotification } = useContext(NotificationsContext);
  const [hosDuration, setHosDuration] = useState<object>();
  const [intItem, setIntIem] = useState(
    item || { ...emptyHistoryItems.hospitalization(patientReference) },
  );
  const [serviceProviders, setServiceProviders] = useState([]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [loading, setLoading] = useComponentLoader();
  const [isServiceProvidersFreeText, setIsServiceProvidersFreeText] = useState(
    item?.serviceProvider?.identifier?.startsWith("other") || false,
  );

  useAsyncEffect(async () => {
    if (serviceProviders.length !== 0) return;
    const orgs = await providerApi.findOrganizations({
      "is-hospital": true,
    });
    const orgReferences = orgs
      .map((o) => createReference("Organization", o.id, o.name))
      .sort((a, b) => a?.display?.localeCompare(b?.display));
    if (orgReferences.length > 0) setServiceProviders(orgReferences);
  }, [serviceProviders]);

  const updateItem = (field, value) => {
    const hospitalization = { ...intItem };
    set(hospitalization, field, value);
    setIntIem(hospitalization);
  };

  const onDelete = () => {
    if (intItem.id) {
      setOpenDialog(true);
    } else {
      try {
        removeHistoryItem();
      } catch (error) {
        console.error(error);
      }
    }
  };

  const deleteHospitalizationHistory = async () => {
    try {
      setLoading(true);
      const hospitalization = { ...intItem };
      const response = await medicalsApi.updateEncounter(
        hospitalization.id,
        {
          ...hospitalization,
          status: "entered-in-error",
        },
        createEncounterParam(encounterReference),
      );
      setIntIem(response);
      updateHistoryItem(response);
      refreshPatientHistory();
      setOpenDialog(false);
      removeHistoryItem();
    } catch (e) {
      console.error(
        `An error occurred while deleting the patient's hospitalization history. Please try again.${e}`,
      );
      setNotification({
        variant: NotificationVariant.error,
        message: e.message,
      });
    } finally {
      setLoading(false);
    }
  };
  const calcDuration = (date1: Date, date2: Date) => {
    if (!date1 && !date2) {
      setHosDuration(undefined);
      return;
    }

    const startFormat: DateValueType = get(
      intItem,
      "period.extension[0].valueCode",
    );
    const endFormat: DateValueType = get(
      intItem,
      "period.extension[1].valueCode",
    );

    let parsedDate1: moment.Moment;
    let parsedDate2: moment.Moment;

    switch (startFormat) {
      case "year only":
        parsedDate1 = moment(date1).startOf("year");
        break;
      case "month and year":
        parsedDate1 = moment(date1).startOf("month");
        break;
      default:
        parsedDate1 = moment(date1 || new Date());
        break;
    }

    switch (endFormat) {
      case "year only":
        parsedDate2 = moment(date2).startOf("year");
        break;
      case "month and year":
        parsedDate2 = moment(date2).startOf("month");
        break;
      default:
        parsedDate2 = moment(date2 || new Date());
        break;
    }

    const duration = moment.duration(parsedDate2.diff(parsedDate1));
    const numOfDays = Math.round(duration.asDays()) + 1;
    if (numOfDays % 365 === 0) {
      setHosDuration({ value: numOfDays / 365, code: "a" });
    } else if (numOfDays % 30 === 0) {
      setHosDuration({ value: numOfDays / 30, code: "mo" });
    } else if (numOfDays % 7 === 0) {
      setHosDuration({ value: numOfDays / 7, code: "wk" });
    } else {
      setHosDuration({ value: numOfDays, code: "d" });
    }
  };

  const saveMedicalCondition = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    try {
      const hospitalization = {
        ...intItem,
      };

      if (patientReference) {
        hospitalization.subject = patientReference;
      }

      if (encounterReference) {
        hospitalization.encounter = encounterReference;
      }

      let response;
      if (hospitalization.id) {
        response = await medicalsApi.updateEncounter(
          hospitalization.id,
          {
            ...hospitalization,
          },
          createEncounterParam(encounterReference),
        );
        setIntIem(response);
      } else {
        response = await medicalsApi.saveEncounter(
          {
            ...hospitalization,
          },
          createEncounterParam(encounterReference),
        );
        setIntIem(response);
      }

      updateHistoryItem(response);
      refreshPatientHistory();

      if (intItem.status === "entered-in-error") {
        try {
          removeHistoryItem();
        } catch (error) {
          console.error(error);
        }
      }

      // can be changed to await
      setEncounterToInProgress();

      setNotification({
        variant: NotificationVariant.success,
        message: "Successfully saved the patient's hospitalization.",
      });

      setSaved(true);
    } catch (e) {
      console.error(
        "An error occurred while saving the patient's sospitalization. Please try again.",
      );
      setNotification({
        variant: NotificationVariant.error,
        message:
          "An error occurred while saving patient's hospitalization. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={saveMedicalCondition}>
        <Grid container spacing={3} xs={12}>
          <DialogPrompt
            openPrompt={openDialog}
            content="You are about to delete a hospitalization item for this patient and this action is permanent. Are you sure?"
            title="Delete Hospitalization History"
            button1="Cancel"
            button2="Delete"
            button1Click={() => setOpenDialog(false)}
            button2Click={deleteHospitalizationHistory}
          />
          <Grid item xs={12} style={{ paddingTop: 10 }}>
            <Grid
              container
              alignContent="center"
              alignItems="center"
              direction="row"
            >
              <Grid item xs={10}>
                {!isServiceProvidersFreeText ? (
                  <FormControl variant="outlined" fullWidth>
                    <TextField
                      select
                      value={get(intItem, "serviceProvider.identifier") || ""}
                      disabled={disabled}
                      onChange={(e) => {
                        const id = e.target.value;
                        const opt = serviceProviders.find(
                          (o) => o.identifier === id,
                        );
                        updateItem("serviceProvider", opt ?? null);
                        setSaved(false);
                      }}
                      label="Hospital/Service Provider"
                      InputLabelProps={{ shrink: true }}
                      required
                    >
                      {serviceProviders.map((p) => (
                        <MenuItem value={p.identifier} key={p.identifier}>
                          {p.display}
                        </MenuItem>
                      ))}
                    </TextField>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    label="Hospital/Service Provider"
                    variant="outlined"
                    value={get(intItem, "serviceProvider.display") || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val || val === "") {
                        updateItem("serviceProvider", blankServiceProviders);
                      } else {
                        updateItem(
                          "serviceProvider",
                          createReference(
                            "Organization",
                            createSlug(
                              val.toLowerCase().replaceAll(" ", "-"),
                              "other",
                            ),
                            val,
                          ),
                        );
                      }
                      setSaved(false);
                    }}
                  />
                )}
              </Grid>
              <Grid item xs={2} pl={2}>
                <FormControlLabel
                  label="Other"
                  disabled={disabled}
                  control={
                    <Checkbox
                      checked={isServiceProvidersFreeText}
                      onChange={(e) => {
                        const { checked } = e.target;
                        setIsServiceProvidersFreeText(checked);
                        if (checked)
                          updateItem("serviceProvider", blankServiceProviders);
                        else updateItem("serviceProvider", null);
                      }}
                    />
                  }
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <TypeAheadInput
              freeOptions
              optionType="snomed"
              subset={[
                "regime/therapy",
                "disorder",
                "procedure",
                "morphologic abnormality",
                "situation",
                "finding",
                "event",
                "person",
                "navigational concept",
              ]}
              value={get(intItem, "reasonReference") || []}
              disabled={disabled}
              variant="outlined"
              required
              onChange={(val, reason) => {
                const references =
                  val?.map((v) =>
                    createReference(v.snomedType, v.snomedCID, v.snomedFSN),
                  ) || [];
                if (reason !== "removeOption") {
                  if (references?.length > 0) {
                    const existingRefs = get(intItem, "reasonReference") || [];
                    updateItem(
                      "reasonReference",
                      unionBy(existingRefs, references, "identifier"),
                    );
                  } else {
                    updateItem("reasonReference", null);
                  }
                  setSaved(false);
                } else {
                  updateItem("reasonReference", references);
                  setSaved(false);
                }
              }}
              isMultiple
              getValueLabel={(v) => get(v, "display")}
              fieldLabel="Reasons for hospitalization"
              getOptionLabel={(o) => o.snomedFSN}
              size="medium"
            />
          </Grid>

          <Grid item xs={12}>
            <DateOptions
              groupLabel="Start"
              groupLabelSize={1}
              disabled={disabled}
              date={intItem.period?.start || null}
              onDateChange={(date) => {
                let { period } = intItem || {};
                if (period) {
                  period.start = date;
                } else {
                  period = { start: date };
                }
                updateItem("period", period);
                calcDuration(date, intItem?.period?.end);
                setSaved(false);
              }}
              onValueTypeChange={(valueType) => {
                const extension = get(intItem, "period.extension") || [];
                extension[0] = {
                  url: "http://example.org/hospitalization-start-date",
                  valueCode: valueType,
                };
                updateItem("period.extension", extension);
              }}
              valueType={get(intItem, "period.extension[0].valueCode") || null}
              maxDate={new Date()}
            />
          </Grid>

          <Grid item xs={12}>
            <DateOptions
              groupLabel="End"
              groupLabelSize={1}
              disabled={disabled}
              date={intItem.period?.end || null}
              onDateChange={(date) => {
                let { period } = intItem || {};
                if (period) {
                  period.end = date;
                } else {
                  period = { end: date };
                }
                updateItem("period", period);
                calcDuration(intItem?.period?.start, date);
                setSaved(false);
              }}
              onValueTypeChange={(valueType) => {
                const extension = get(intItem, "period.extension") || [];
                extension[1] = {
                  url: "http://example.org/hospitalization-end-date",
                  valueCode: valueType,
                };
                updateItem("period.extension", extension);
              }}
              valueType={get(intItem, "period.extension[1].valueCode") || null}
              maxDate={new Date()}
            />
          </Grid>

          <Grid item xs={6} md={6}>
            <Quantity
              fieldLabel="Duration"
              quantity={{ ...get(intItem, "length"), ...hosDuration }}
              setQuantity={(val) => {
                updateItem("length", val);
                setHosDuration(val.value);
                setSaved(false);
              }}
              disabled={false}
              defaultUnitCode="d"
              optionKey="timeSpans"
              codingSystem={CodingSystems.UNITS_OF_MEASURE}
              size={undefined}
              min={0}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              disabled={disabled}
              value={get(intItem, "status", undefined) || null}
              onChange={(e) => {
                updateItem("status", e.target.value);
              }}
              label="Status"
              InputLabelProps={{ shrink: true }}
            >
              {hospitalizationHistoryStatuses.map((c, i) => (
                <MenuItem value={c} key={`hospitalization-status-${i}`}>
                  {generateHospitalizationStatusDisplay(c)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" sx={{ gap: 1 }}>
              <Button
                color="secondary"
                variant="outlined"
                size="small"
                disabled={disabled}
                onClick={onDelete}
              >
                Delete
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                size="small"
                disabled={disabled}
              >
                Save
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
      <ComponentLoader loading={loading} />
    </>
  );
};

export default HospitalizationHistoryForm;
